import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Event } from '../types/Event';
import './NiceMobileCalendar.css';
import apiService from '../services/api';
import MonthYearPicker from './MonthYearPicker';

/* =========================================================
   NiceMobileCalendar
   Google Calendar-style mobile calendar using Sereno design
   tokens. Views: day · 3-day · week · month · schedule.
   Gestures: horizontal swipe to navigate, tap empty slot to
   create, tap event to edit, today button, now-line.
   ========================================================= */

type ViewKind = 'day' | 'three-day' | 'week' | 'month' | 'schedule';

interface Props {
  events: Event[];
  onEventClick: (event: Event) => void;
  onDateClick: (slotInfo: { start: Date; end: Date }) => void;
  onEventDrop: (info: { event: Event; start: Date; end: Date }) => void;
  onEventResize: (info: { event: Event; start: Date; end: Date }) => void;
}

const HOUR_HEIGHT = 56;
const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;

// Patient-confirmation state derived from the appointment fields.
// confirmed → ✓, cancelled → ✕ (faded + struck), awaiting reply → ⏱.
type ConfirmState = 'confirmed' | 'cancelled' | 'pending';
const CONFIRM_GLYPH: Record<ConfirmState, string> = { confirmed: '✓', cancelled: '✕', pending: '⏱' };
const confirmStateOf = (ev: Event): ConfirmState | null => {
  const cs = (ev.confirmationStatus || '').toLowerCase();
  if (cs === 'confirmed') return 'confirmed';
  if (cs === 'cancelled') return 'cancelled';
  if (ev.confirmationLinkSentAt && (cs === '' || cs === 'pending' || cs === 'no_response')) return 'pending';
  return null;
};
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);

const fmtWeekdayShort = (locale: string, d: Date) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
const fmtWeekdayNarrow = (locale: string, d: Date) =>
  new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(d);
const fmtMonthLong = (locale: string, d: Date) =>
  new Intl.DateTimeFormat(locale, { month: 'long' }).format(d);
const fmtMonthShort = (locale: string, d: Date) =>
  new Intl.DateTimeFormat(locale, { month: 'short' }).format(d);

const startOfDay = (d: Date): Date => {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
};
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
};
const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const startOfWeek = (d: Date): Date => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay()); // Sunday-start
  return x;
};
const formatHM = (d: Date): string =>
  `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

const viewDayCount = (v: ViewKind): number => (v === 'day' ? 1 : v === 'three-day' ? 3 : v === 'week' ? 7 : 0);

const NiceMobileCalendar: React.FC<Props> = ({ events, onEventClick, onDateClick, onEventDrop }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useState<ViewKind>(() => {
    return (localStorage.getItem('nmc:view') as ViewKind) || 'day';
  });
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [now, setNow] = useState<Date>(new Date());
  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const titleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { localStorage.setItem('nmc:view', view); }, [view]);

  // Fetch clinic-wide block overrides for the visible month range
  useEffect(() => {
    const from = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1).toISOString().slice(0, 10);
    const to = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0).toISOString().slice(0, 10);
    (apiService.get(`/api/booking-admin/overrides?from=${from}&to=${to}`) as Promise<any>).then(r => {
      if (!r.success) return;
      const dates = new Set<string>(
        (r.data as any[]).filter((o: any) => o.type === 'block' && (o.userId == null || o.user_id == null)).map((o: any) => o.date as string)
      );
      setHolidaySet(dates);
    }).catch(() => {});
  }, [anchor.getFullYear(), anchor.getMonth()]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick the now-line every minute
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Ensure anchor makes sense when view changes (snap week anchor to start of week)
  useEffect(() => {
    if (view === 'week') setAnchor(a => startOfWeek(a));
    if (view === 'three-day') setAnchor(a => startOfDay(a));
  }, [view]);

  // ------- Navigation -------
  const step = (dir: 1 | -1) => {
    setAnchor(a => {
      if (view === 'month') {
        const x = new Date(a.getFullYear(), a.getMonth() + dir, 1);
        return x;
      }
      if (view === 'week') return addDays(a, 7 * dir);
      if (view === 'three-day') return addDays(a, 3 * dir);
      if (view === 'schedule') return addDays(a, 7 * dir);
      return addDays(a, dir);
    });
  };

  const goToday = () => setAnchor(view === 'week' ? startOfWeek(new Date()) : view === 'month' ? new Date(new Date().getFullYear(), new Date().getMonth(), 1) : startOfDay(new Date()));

  const handleMonthYearSelect = (year: number, month: number) => {
    const base = new Date(year, month, 1);
    if (view === 'week') setAnchor(startOfWeek(base));
    else if (view === 'three-day' || view === 'day') setAnchor(startOfDay(base));
    else setAnchor(base);
    setPickerOpen(false);
  };

  // ------- Header title -------
  const title = useMemo(() => {
    const today = new Date();
    if (view === 'month' || view === 'schedule') {
      return `${fmtMonthLong(locale, anchor)} ${anchor.getFullYear()}`;
    }
    if (view === 'week') {
      const wEnd = addDays(anchor, 6);
      if (anchor.getMonth() !== wEnd.getMonth()) {
        return `${anchor.getDate()} ${fmtMonthShort(locale, anchor)} – ${wEnd.getDate()} ${fmtMonthShort(locale, wEnd)}`;
      }
      return `${anchor.getDate()}–${wEnd.getDate()} ${fmtMonthShort(locale, anchor)}`;
    }
    if (view === 'three-day') {
      const end = addDays(anchor, 2);
      if (anchor.getMonth() !== end.getMonth()) {
        return `${anchor.getDate()} ${fmtMonthShort(locale, anchor)} – ${end.getDate()} ${fmtMonthShort(locale, end)}`;
      }
      return `${anchor.getDate()}–${end.getDate()} ${fmtMonthShort(locale, anchor)}`;
    }
    // day
    if (sameDay(anchor, today)) return t('calendar.today');
    if (sameDay(anchor, addDays(today, 1))) return t('calendar.tomorrow');
    if (sameDay(anchor, addDays(today, -1))) return t('calendar.yesterday');
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(anchor);
  }, [view, anchor, locale, t]);

  const eyebrow = useMemo(() => {
    const d = view === 'month' ? new Date() : anchor;
    return `${fmtWeekdayShort(locale, d)} · ${d.getDate()} ${fmtMonthShort(locale, d)}`;
  }, [view, anchor, locale]);

  // ------- Date strip window (14-day rolling) -------
  const stripDays = useMemo(() => {
    const base = view === 'week' ? anchor : addDays(anchor, -3);
    return Array.from({ length: 14 }, (_, i) => addDays(base, i));
  }, [anchor, view]);

  // ------- Event indexing -------
  const eventsByDayKey = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const ev of events) {
      const s = new Date(ev.start);
      const k = `${s.getFullYear()}-${s.getMonth()}-${s.getDate()}`;
      const arr = map.get(k) ?? [];
      arr.push(ev);
      map.set(k, arr);
    }
    for (const arr of Array.from(map.values())) {
      arr.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }
    return map;
  }, [events]);

  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const getDayEvents = useCallback((d: Date): Event[] => eventsByDayKey.get(dayKey(d)) ?? [], [eventsByDayKey]);

  // ------- Swipe gestures for body -------
  const bodyRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number; dx: number; active: boolean; horizontal: boolean | null }>({
    x: 0, y: 0, dx: 0, active: false, horizontal: null,
  });
  const SWIPE_THRESHOLD = 60;
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    swipe.current = { x: t.clientX, y: t.clientY, dx: 0, active: true, horizontal: null };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!swipe.current.active) return;
    const t = e.touches[0];
    const dx = t.clientX - swipe.current.x;
    const dy = t.clientY - swipe.current.y;
    if (swipe.current.horizontal === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        swipe.current.horizontal = Math.abs(dx) > Math.abs(dy);
      }
    }
    if (swipe.current.horizontal === true) {
      swipe.current.dx = dx;
      if (paneRef.current) {
        paneRef.current.classList.add('dragging');
        paneRef.current.style.transform = `translateX(${dx}px)`;
      }
    }
  };
  const onTouchEnd = () => {
    if (!swipe.current.active) return;
    const dx = swipe.current.dx;
    swipe.current.active = false;
    if (paneRef.current) {
      paneRef.current.classList.remove('dragging');
      paneRef.current.style.transform = '';
    }
    if (swipe.current.horizontal && Math.abs(dx) > SWIPE_THRESHOLD) {
      step(dx < 0 ? 1 : -1);
    }
  };

  // ------- Long-press drag-and-drop for events (mobile) -------
  const LONG_PRESS_MS = 400;
  const dragState = useRef<{
    event: Event;
    el: HTMLElement;
    startX: number;
    startY: number;
    grabOffsetY: number;
    durationMs: number;
    longPressTimer: number | null;
    active: boolean;
    newStart: Date | null;
    newEnd: Date | null;
  } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const cleanupDrag = (commit: boolean) => {
    const ds = dragState.current;
    if (!ds) return;
    if (ds.longPressTimer) window.clearTimeout(ds.longPressTimer);
    if (ds.active) {
      ds.el.style.transform = '';
      ds.el.style.zIndex = '';
      ds.el.style.opacity = '';
      ds.el.style.boxShadow = '';
      setDraggingId(null);
      if (commit && ds.newStart && ds.newEnd) {
        const origStart = new Date(ds.event.start).getTime();
        if (ds.newStart.getTime() !== origStart) {
          onEventDrop({ event: ds.event, start: ds.newStart, end: ds.newEnd });
        }
      }
    }
    dragState.current = null;
  };

  const onEvtTouchStart = (e: React.TouchEvent<HTMLDivElement>, ev: Event) => {
    if (ev.isShared) return;
    if (e.touches.length !== 1) return;
    e.stopPropagation();
    const t = e.touches[0];
    const el = e.currentTarget;
    const startMs = new Date(ev.start).getTime();
    const endMs = new Date(ev.end).getTime();
    const elRect = el.getBoundingClientRect();
    dragState.current = {
      event: ev,
      el,
      startX: t.clientX,
      startY: t.clientY,
      grabOffsetY: t.clientY - elRect.top,
      durationMs: endMs - startMs,
      longPressTimer: null,
      active: false,
      newStart: null,
      newEnd: null,
    };
    dragState.current.longPressTimer = window.setTimeout(() => {
      const ds = dragState.current;
      if (!ds) return;
      ds.active = true;
      try { navigator.vibrate?.(40); } catch { /* noop */ }
      setDraggingId(ev.id);
      ds.el.style.zIndex = '20';
      ds.el.style.opacity = '0.88';
      ds.el.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
    }, LONG_PRESS_MS);
  };

  const onEvtTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const ds = dragState.current;
    if (!ds) return;
    e.stopPropagation();
    const t = e.touches[0];
    const dx = t.clientX - ds.startX;
    const dy = t.clientY - ds.startY;
    if (!ds.active) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        if (ds.longPressTimer) window.clearTimeout(ds.longPressTimer);
        dragState.current = null;
      }
      return;
    }
    if (e.cancelable) e.preventDefault();
    ds.el.style.transform = `translate(${dx}px, ${dy}px)`;

    // Find target column under finger
    const prevPe = ds.el.style.pointerEvents;
    ds.el.style.pointerEvents = 'none';
    const target = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
    ds.el.style.pointerEvents = prevPe;
    const col = target?.closest('.nmc-col') as HTMLElement | null;
    if (col && col.dataset.dayIso) {
      const day = new Date(col.dataset.dayIso);
      const bounds = col.getBoundingClientRect();
      // Snap to where the TOP of the chip lands, not where the finger is
      const chipTopY = t.clientY - ds.grabOffsetY;
      const yInGrid = Math.max(0, chipTopY - bounds.top);
      const slots = Math.round(yInGrid / (HOUR_HEIGHT / 4));
      const minutes = Math.max(0, slots * 15);
      const newStart = new Date(day);
      newStart.setHours(DAY_START_HOUR, 0, 0, 0);
      newStart.setMinutes(newStart.getMinutes() + minutes);
      const newEnd = new Date(newStart.getTime() + ds.durationMs);
      ds.newStart = newStart;
      ds.newEnd = newEnd;
    }
  };

  const onEvtTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    e.stopPropagation();
    cleanupDrag(true);
  };

  const onEvtTouchCancel = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    e.stopPropagation();
    cleanupDrag(false);
  };

  // ------- Time-grid helpers -------
  const columnsFor = (): Date[] => {
    const n = viewDayCount(view);
    const start = view === 'week' ? startOfWeek(anchor) : view === 'three-day' ? anchor : anchor;
    return Array.from({ length: n }, (_, i) => addDays(start, i));
  };

  const timeGridRef = useRef<HTMLDivElement>(null);
  // Scroll to current time or 8am initially / on view or day change.
  // Must NOT depend on `now`: it ticks every minute and would yank the user's
  // scroll position back, making the end of the day unreachable.
  const anchorKey = startOfDay(anchor).getTime();
  useEffect(() => {
    if (!timeGridRef.current) return;
    const targetHour = sameDay(anchor, new Date()) ? Math.max(DAY_START_HOUR, new Date().getHours() - 1) : 8;
    const y = (targetHour - DAY_START_HOUR) * HOUR_HEIGHT;
    timeGridRef.current.scrollTop = y;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, anchorKey]);

  const handleSlotTap = (day: Date, clientY: number, bounds: DOMRect) => {
    const yInGrid = clientY - bounds.top;
    const minutes = Math.max(0, Math.floor(yInGrid / (HOUR_HEIGHT / 4)) * 15);
    const start = new Date(day);
    start.setHours(DAY_START_HOUR, 0, 0, 0);
    start.setMinutes(start.getMinutes() + minutes);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    onDateClick({ start, end });
  };

  // Compute event position inside a day column
  const eventRect = (ev: Event, dayStart: Date): { top: number; height: number } | null => {
    const s = new Date(ev.start);
    const e = new Date(ev.end);
    const dayEnd = addDays(dayStart, 1);
    if (e <= dayStart || s >= dayEnd) return null;
    const clampS = s < dayStart ? dayStart : s;
    const clampE = e > dayEnd ? dayEnd : e;
    // Minutes since the day's start (not getHours()): an event ending at
    // midnight is clamped to dayEnd, whose getHours() is 0, not 24.
    const startMin = (clampS.getTime() - dayStart.getTime()) / 60000 - DAY_START_HOUR * 60;
    const endMin = (clampE.getTime() - dayStart.getTime()) / 60000 - DAY_START_HOUR * 60;
    const top = (startMin / 60) * HOUR_HEIGHT;
    const height = Math.max(20, ((endMin - startMin) / 60) * HOUR_HEIGHT);
    return { top, height };
  };

  // ------- Renderers -------
  const renderTimeGrid = () => {
    const cols = columnsFor();
    const today = new Date();
    const colsClass = view === 'day' ? '' : view === 'three-day' ? 'cols-3' : 'cols-7';
    const gutterColSpan = 1;

    return (
      <div className="nmc-grid" ref={timeGridRef}>
        <div className={`nmc-grid-inner ${colsClass}`} style={{ gridTemplateRows: `auto repeat(${HOURS.length}, ${HOUR_HEIGHT}px)` }}>
          {/* Corner gutter header */}
          <div className="nmc-gutter-head" style={{ gridColumn: 1, gridRow: 1 }} />
          {/* Column headers (only in multi-day views) */}
          {cols.length > 1 && cols.map((d, i) => {
            const isToday = sameDay(d, today);
            return (
              <div
                key={`head-${i}`}
                className={`nmc-colhead ${isToday ? 'today' : ''}`}
                style={{ gridColumn: i + 2, gridRow: 1 }}
                onClick={() => { setView('day'); setAnchor(startOfDay(d)); }}
              >
                <div>{fmtWeekdayShort(locale, d)}</div>
                <div className="dn">{d.getDate()}</div>
              </div>
            );
          })}
          {/* Hour labels in gutter */}
          {HOURS.map((h, i) => (
            <div
              key={`lbl-${h}`}
              className="nmc-time-label"
              style={{ gridColumn: 1, gridRow: i + 2 }}
            >
              {h.toString().padStart(2, '0')}:00
            </div>
          ))}
          {/* Day columns — each column is one grid cell spanning all hour rows, positioned via absolute */}
          {cols.map((day, colIdx) => {
            const isToday = sameDay(day, today);
            const dayStart = startOfDay(day);
            const dayEvents = getDayEvents(day);
            return (
              <div
                key={`col-${colIdx}`}
                className={`nmc-col ${isToday ? 'today' : ''}`}
                data-day-iso={dayStart.toISOString()}
                style={{
                  gridColumn: colIdx + 1 + gutterColSpan,
                  gridRow: `2 / span ${HOURS.length}`,
                  position: 'relative',
                }}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('.nmc-evt')) return;
                  const bounds = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  handleSlotTap(day, e.clientY, bounds);
                }}
              >
                {/* Hour lines */}
                {HOURS.map((_, i) => (
                  <div key={i} className="nmc-hour half" style={{ position: 'absolute', left: 0, right: 0, top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }} />
                ))}
                {/* Now line */}
                {isToday && now.getHours() >= DAY_START_HOUR && now.getHours() < DAY_END_HOUR && (
                  <div
                    className="nmc-now"
                    style={{ top: ((now.getHours() - DAY_START_HOUR) * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT }}
                  />
                )}
                {/* Events */}
                {dayEvents.map(ev => {
                  const r = eventRect(ev, dayStart);
                  if (!r) return null;
                  const cst = confirmStateOf(ev);
                  return (
                    <div
                      key={ev.id}
                      className={`nmc-evt ${draggingId === ev.id ? 'dragging' : ''}${cst ? ` confirm-${cst}` : ''}${ev.isShared ? ' nmc-evt-shared' : ''}`}
                      style={{
                        top: r.top,
                        height: r.height,
                        background: ev.isShared
                          ? `color-mix(in srgb, ${ev.color || 'var(--accent)'} 18%, transparent)`
                          : ev.color || 'var(--accent)',
                        border: ev.isShared ? `2px solid ${ev.color || 'var(--accent)'}` : undefined,
                        color: ev.isShared ? (ev.color || 'var(--accent)') : undefined,
                      }}
                      onClick={(ce) => { ce.stopPropagation(); if (draggingId === ev.id) return; if (!ev.isShared) onEventClick(ev); }}
                      onTouchStart={(te) => onEvtTouchStart(te, ev)}
                      onTouchMove={onEvtTouchMove}
                      onTouchEnd={onEvtTouchEnd}
                      onTouchCancel={onEvtTouchCancel}
                    >
                      {cst && (
                        <span className={`nmc-confirm-badge confirm-${cst}`} title={t(`calendar.confirmation.${cst}`)} aria-label={t(`calendar.confirmation.${cst}`)}>
                          {CONFIRM_GLYPH[cst]}
                        </span>
                      )}
                      <div className="nmc-evt-body">
                        {[ev.clientName, ev.title].filter(Boolean).join(' · ')}
                      </div>
                      {ev.resources && ev.resources.length > 0 && (
                        <div className="nmc-evt-rooms" style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: '0.7rem', opacity: 0.9, marginTop: 1 }}>
                          {ev.resources.map(r => (
                            <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 2, background: r.color || '#6366f1', flexShrink: 0 }} />
                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonth = () => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const today = new Date();
    return (
      <div className="nmc-month">
        <div className="nmc-month-dow">
          {Array.from({ length: 7 }, (_, i) => {
              const ref = new Date(2000, 0, 2 + i); // Jan 2 2000 = Sunday
              return <span key={i}>{fmtWeekdayNarrow(locale, ref)}</span>;
            })}
        </div>
        <div className="nmc-month-grid">
          {cells.map((d, i) => {
            const isCur = d.getMonth() === anchor.getMonth();
            const isToday = sameDay(d, today);
            const isSel = sameDay(d, anchor);
            const isHoliday = holidaySet.has(d.toISOString().slice(0, 10));
            const dayEvs = getDayEvents(d);
            return (
              <div
                key={i}
                className={`nmc-month-cell ${!isCur ? 'other' : ''} ${isToday ? 'today' : ''} ${isSel ? 'selected' : ''} ${isHoliday ? 'holiday' : ''}`}
                onClick={() => { setAnchor(startOfDay(d)); setView('day'); }}
              >
                <div className="nmc-month-num">{d.getDate()}</div>
                {dayEvs.slice(0, 2).map(ev => {
                  const cst = confirmStateOf(ev);
                  return (
                    <div key={ev.id} className={`nmc-month-chip${cst ? ` confirm-${cst}` : ''}`} style={{ borderLeftColor: ev.color || undefined, background: ev.color ? `color-mix(in srgb, ${ev.color} 16%, transparent)` : undefined, color: ev.color || undefined }}>
                      {cst && <span className="nmc-chip-glyph" aria-hidden="true">{CONFIRM_GLYPH[cst]} </span>}
                      {formatHM(new Date(ev.start))} {ev.title}
                    </div>
                  );
                })}
                {dayEvs.length > 2 && (
                  <div className="nmc-month-dots">
                    {dayEvs.slice(2, 5).map((_, j) => <span key={j} className="nmc-month-dot" />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSchedule = () => {
    const start = startOfDay(anchor);
    const days: { date: Date; evs: Event[] }[] = [];
    for (let i = 0; i < 30; i++) {
      const d = addDays(start, i);
      const evs = getDayEvents(d);
      if (evs.length > 0 || i < 3) days.push({ date: d, evs });
    }
    const today = new Date();
    return (
      <div className="nmc-schedule">
        {days.map(({ date, evs }) => {
          const isToday = sameDay(date, today);
          const label = `${fmtWeekdayShort(locale, date)}, ${date.getDate()} ${fmtMonthShort(locale, date)}${isToday ? ` · ${t('calendar.today').toLowerCase()}` : ''}`;
          return (
            <div key={date.toISOString()}>
              <div className={`nmc-sch-day ${isToday ? 'today' : ''}`}>{label}</div>
              {evs.length === 0 && <div className="nmc-sch-empty">{t('calendar.noAppointments')}</div>}
              {evs.map(ev => {
                const s = new Date(ev.start);
                const e = new Date(ev.end);
                const cst = confirmStateOf(ev);
                return (
                  <button key={ev.id} type="button" className={`nmc-card${cst ? ` confirm-${cst}` : ''}${ev.isShared ? ' nmc-card-shared' : ''}`} style={{ borderColor: ev.color ? `${ev.color}55` : undefined, width: '100%', textAlign: 'left', background: ev.isShared && ev.color ? `color-mix(in srgb, ${ev.color} 8%, transparent)` : 'none', touchAction: 'manipulation', cursor: 'pointer', opacity: ev.isShared ? 0.8 : 1 }} onClick={() => { if (!ev.isShared) onEventClick(ev); }}>
                    <div className="t-col">
                      <div className="t-start">{formatHM(s)}</div>
                      <div className="t-end">{formatHM(e)}</div>
                    </div>
                    <div className="c-col">
                      <div className="c-title">{ev.title}</div>
                      {ev.clientName && <div className="c-meta">{ev.clientName}</div>}
                      {ev.resources && ev.resources.length > 0 && (
                        <div className="c-meta" style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          {ev.resources.map(r => (
                            <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <span style={{ width: 7, height: 7, borderRadius: 2, background: r.color || '#6366f1', flexShrink: 0 }} />
                              {r.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {cst && (
                      <span className={`nmc-confirm-badge card confirm-${cst}`} title={t(`calendar.confirmation.${cst}`)} aria-label={t(`calendar.confirmation.${cst}`)}>
                        {CONFIRM_GLYPH[cst]}
                      </span>
                    )}
                    <style>{`.nice-mcal .nmc-card::before { background: ${ev.color || 'var(--accent)'}; }`}</style>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const handleFab = () => {
    const start = new Date();
    start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    onDateClick({ start, end });
  };

  const dayKeyStr = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  return (
    <div className="nice-mcal">
      {/* ---- Header ---- */}
      <div className="nmc-header">
        <div className="nmc-eyebrow">
          <span>{eyebrow}</span>
          <span>{events.length > 0 ? t('briefing.widget.appointments', { count: events.length }) : '—'}</span>
        </div>
        <div className="nmc-title-row">
          <button
            type="button"
            ref={titleRef}
            className="nmc-title"
            onClick={() => setPickerOpen(o => !o)}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            aria-label={t('calendar.jumpToDate')}
          >
            {title}
          </button>
          <button type="button" className="nmc-today-btn" onClick={goToday}>{t('common.today')}</button>
        </div>
        <MonthYearPicker
          isOpen={pickerOpen}
          anchorEl={titleRef.current}
          year={anchor.getFullYear()}
          month={anchor.getMonth()}
          locale={locale}
          onSelect={handleMonthYearSelect}
          onClose={() => setPickerOpen(false)}
        />
        <div className="nmc-toolbar">
          <div className="nmc-nav">
            <button type="button" aria-label={t('common.previous')} onClick={() => step(-1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button type="button" aria-label={t('common.next')} onClick={() => step(1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="nmc-view-seg" role="tablist">
            {([
              ['day', t('calendar.day')],
              ['three-day', '3d'],
              ['week', t('calendar.week').slice(0, 3)],
              ['month', t('calendar.month')],
              ['schedule', t('calendar.agenda')],
            ] as [ViewKind, string][]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={view === key ? 'on' : ''}
                onClick={() => setView(key)}
              >{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Date strip (only for time-grid views) ---- */}
      {(view === 'day' || view === 'three-day' || view === 'week') && (
        <div className="nmc-strip">
          {stripDays.map(d => {
            const isToday = sameDay(d, new Date());
            const selected = view === 'day'
              ? sameDay(d, anchor)
              : view === 'three-day'
                ? (d >= anchor && d < addDays(anchor, 3))
                : (d >= anchor && d < addDays(anchor, 7));
            const hasEvents = getDayEvents(d).length > 0;
            return (
              <div
                key={dayKeyStr(d)}
                className={`d ${selected ? 'on' : ''} ${isToday ? 'today' : ''} ${hasEvents ? 'has-events' : ''}`}
                onClick={() => {
                  if (view === 'day') setAnchor(startOfDay(d));
                  else if (view === 'three-day') setAnchor(startOfDay(d));
                  else setAnchor(startOfWeek(d));
                }}
              >
                <div className="dow">{fmtWeekdayNarrow(locale, d)}</div>
                <div className="dn">{d.getDate()}</div>
                <div className="mark" />
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Body (swipeable) ---- */}
      <div
        className="nmc-body"
        ref={bodyRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="nmc-pane" ref={paneRef}>
          {(view === 'day' || view === 'three-day' || view === 'week') && renderTimeGrid()}
          {view === 'month' && renderMonth()}
          {view === 'schedule' && renderSchedule()}
        </div>
      </div>

      {/* ---- FAB ---- */}
      <button type="button" className="nmc-fab" aria-label={t('calendar.newEvent')} onClick={handleFab}>
        +
      </button>
    </div>
  );
};

export default NiceMobileCalendar;
